import { toast } from "@/components/ui";
import { BodyText } from "@/components/ui/Text";
import { useBatchAddMedicalHistory } from "@/features/Patient/MedicalRecords/MedicalHistory/network";
import { useState } from "react";
import { CiqConditionPopover } from "./CiqConditionPopover";
import { CiqConditionSearch } from "./CiqConditionSearch";
import { CiqConditionSingleRow } from "./CiqConditionSingleRow";
import type { ProcessedCondition, SnomedCode } from "./useAmazonVisitIntake";
import { useMarkConditionAddedToChart } from "./useMarkConditionAddedToChart";

interface CiqConditionProps {
  condition: ProcessedCondition;
  patientId: string;
  amazonVisitId?: string;
  questionnaireResponseId?: string;
  onConditionAddSuccess?: (condition: ProcessedCondition) => void;
}

export const CiqCondition = ({
  condition,
  patientId,
  amazonVisitId,
  questionnaireResponseId,
  onConditionAddSuccess,
}: CiqConditionProps) => {
  const [addedCode, setAddedCode] = useState<SnomedCode | undefined>(
    condition.snomedCodes?.find((c) => c.addedToChartAt),
  );

  const {
    mutateAsync: batchAddMedicalHistory,
    isPending,
    variables,
  } = useBatchAddMedicalHistory({ patientId });

  const { mutateAsync: markConditionAddedToChart } =
    useMarkConditionAddedToChart({ patientId, amazonVisitId });

  const pendingCodeDisplay = isPending
    ? variables?.conditionCodes?.[0]?.text
    : undefined;

  const singleCode =
    condition.snomedCodes?.length === 1 ? condition.snomedCodes[0] : null;

  const handleAdd = async (snomedCode: SnomedCode) => {
    try {
      await batchAddMedicalHistory({
        patientId,
        conditionCodes: [
          {
            coding: [
              {
                code: snomedCode.code,
                display: snomedCode.display,
                system: snomedCode.system,
              },
            ],
            text: snomedCode.display,
          },
        ],
      });
      setAddedCode(snomedCode);
    } catch {
      toast.error(`Failed to add ${snomedCode.display} to medical history`);
      return;
    }

    if (questionnaireResponseId) {
      try {
        await markConditionAddedToChart({
          questionnaireResponseId,
          conditionCode: snomedCode.code,
          conditionCodeSystem: snomedCode.system,
        });
      } catch {}
    }

    onConditionAddSuccess?.(condition);
  };

  const noSnomedCodes = !condition.snomedCodes?.length;

  return (
    <div className="border-l-4 border-ai pl-2 py-0.5 rounded">
      <BodyText size="sm" bold>
        {condition.term.replace(/\b\w/g, (c) => c.toUpperCase())}
      </BodyText>
      {noSnomedCodes ? (
        <CiqConditionSearch
          patientId={patientId}
          defaultSearchTerm={condition.term}
          onAdd={handleAdd}
          isPending={isPending}
          addedCode={addedCode}
          pendingCodeDisplay={pendingCodeDisplay}
        />
      ) : singleCode ? (
        <CiqConditionSingleRow
          snomedCode={singleCode}
          onAdd={handleAdd}
          isPending={isPending}
          isAdded={addedCode?.code === singleCode.code}
        />
      ) : (
        <CiqConditionPopover
          snomedCodes={condition.snomedCodes}
          onAdd={handleAdd}
          isPending={isPending}
          addedCode={addedCode}
          pendingCodeDisplay={pendingCodeDisplay}
        />
      )}
    </div>
  );
};
